// Вотчер in-app уведомлений (SPEC §4.5). Подписан на ДОМЕННОЕ состояние стора (chatList), а не на
// сырой transport.events() — так app-ui остаётся «все данные через client-store» (инв.2). На рост
// lastMessage.seq в чате (новое committed-сообщение) достаёт полное Message из timeline, применяет
// чистое решение decideInAppNotification (активный чат/mute/режим/звук) и пушит тост + опц. звук.
//
// Старт: запоминаем текущие seq как «уже видели» (без всплеска тостов по истории при входе).

import { useEffect, useRef } from 'react'
import type { ChatId, Message } from '@tele-chat/shared-contracts'
import type { TimelineEntry } from '@tele-chat/client-store'
import { useNavigate } from 'react-router-dom'
import { useStore } from '../store/StoreContext.js'
import { useSelf } from '../auth/SessionContext.js'
import { useActiveChat } from '../app/ActiveChatContext.js'
import { useNotifySettings } from '../settings/NotifySettingsContext.js'
import { useNotifications } from './NotificationsContext.js'
import { decideInAppNotification } from './decide.js'
import { buildMessageDeepLink } from '../routing/deep-link.js'
import { strings } from '../i18n/strings.js'

/** Является ли элемент ленты committed-сообщением (а не оптимистичным хвостом). */
function isCommitted(entry: TimelineEntry): entry is Message {
  return !('optimistic' in entry)
}

/** Последнее committed-сообщение чата (для решения по уведомлению). */
function lastCommitted(timeline: readonly TimelineEntry[]): Message | undefined {
  for (let i = timeline.length - 1; i >= 0; i--) {
    const e = timeline[i]
    if (e !== undefined && isCommitted(e)) return e
  }
  return undefined
}

/**
 * Подключает вотчер. Вызывается один раз внутри AppShell (когда есть self + роутер). Возвращает void;
 * весь эффект — побочный (тосты/звук).
 */
export function useInAppNotifications(): void {
  const store = useStore()
  const self = useSelf()
  const { activeChatId, windowFocused } = useActiveChat()
  const { settings } = useNotifySettings()
  const { notify, playSound } = useNotifications()
  const navigate = useNavigate()

  // Последний увиденный seq по чату (чтобы не дублировать тост и не всплывать по истории).
  const seenSeq = useRef<Map<ChatId, number>>(new Map())
  const initialized = useRef(false)

  // Держим свежие зависимости в ref, чтобы единственная подписка не пересоздавалась на каждый рендер.
  const deps = useRef({ self, activeChatId, windowFocused, settings })
  deps.current = { self, activeChatId, windowFocused, settings }

  useEffect(() => {
    if (self === null) return

    const seedSeen = (): void => {
      for (const chat of store.select.chatList()) {
        const seq = chat.lastMessage?.seq ?? 0
        seenSeq.current.set(chat.id, seq)
      }
      initialized.current = true
    }

    if (!initialized.current) seedSeen()

    const handle = (): void => {
      const d = deps.current
      if (d.self === null) return
      for (const chat of store.select.chatList()) {
        const seq = chat.lastMessage?.seq ?? 0
        const prev = seenSeq.current.get(chat.id) ?? 0
        if (seq <= prev) continue
        seenSeq.current.set(chat.id, seq)

        // Новое (или несколько) — берём самое свежее committed-сообщение для решения.
        const msg = lastCommitted(store.select.timeline(chat.id))
        if (msg === undefined || msg.seq <= prev) continue

        const selfMessageReplied =
          msg.replyTo !== undefined && msg.replyTo.senderId === d.self.id

        const decision = decideInAppNotification(
          msg,
          {
            selfId: d.self.id,
            activeChatId: d.activeChatId,
            windowFocused: d.windowFocused,
            muted: chat.myState.muted,
            settings: d.settings,
          },
          selfMessageReplied,
        )
        if (!decision.toast) continue

        const title = chat.title ?? strings.notif.newMessage
        const body = decision.showPreview ? (msg.text ?? previewByKind(msg)) : undefined
        notify({
          kind: 'message',
          title,
          body,
          chatId: chat.id,
          messageId: msg.id,
          action: {
            label: strings.nav.chats,
            run: () => navigate(buildMessageDeepLink(chat.id, msg.id)),
          },
        })
        if (decision.sound) playSound()
      }
    }

    const unsubscribe = store.subscribe(handle)
    return unsubscribe
  }, [store, self, notify, playSound, navigate])
}

/** Короткое превью по типу для тоста без текста (медиа/голос). */
function previewByKind(msg: Message): string {
  switch (msg.kind) {
    case 'media':
      return '🖼 Фото'
    case 'voice':
      return '🎤 Голосовое'
    default:
      return ''
  }
}
